import React from 'react';
import { Sun, FlaskConical, Leaf, Activity } from 'lucide-react';

/**
 * VedicBiophysics — the named scientific pillar of the house.
 *
 * A single editorial component reused on both Home (philosophy area)
 * and Transparency (after the five-step provenance). The narrative is
 * fixed; only the section overline can be tuned per surface.
 *
 *   "Vedic Biophysics is the study of how ancestral botanical
 *    signatures and circadian light frequencies interact with human
 *    biological systems."
 *
 * The frame elevates the Atreya concierge from a "guide" into a
 * "Ritual Optimizer" working from a science, not a tradition alone.
 */

const PILLARS = [
  {
    id: 'signatures',
    icon: Leaf,
    overline: 'Pillar I',
    title: 'Botanical signatures.',
    body:
      'Every plant carries a measurable molecular signature — phenols, terpenes, oligosaccharides — that the ancients named through ritual long before chromatography could measure them. Our base, 100 % Kewra, was selected for the precision of its signature, not the romance of its scent.',
  },
  {
    id: 'circadian',
    icon: Sun,
    overline: 'Pillar II',
    title: 'Circadian light frequencies.',
    body:
      'Skin is a circadian organ. It reads the colour and angle of the sun and rewrites its own repair schedule overnight. Our Soul Night Oil and First Light Ritual are built around the two narrow windows when the human dermis is most receptive — recorded in the Vedas as the Brahma Muhurta, recorded in modern chronobiology as the 04:00–06:00 cortisol trough.',
  },
  {
    id: 'systems',
    icon: Activity,
    overline: 'Pillar III',
    title: 'Human biological systems.',
    body:
      'We do not formulate for one molecule and one symptom. We formulate for the panchamahabhuta — the five-system biology of touch, temperature, vessel, breath and rhythm. Every ritual is timed against one of these systems and against the hour the system is open.',
  },
  {
    id: 'method',
    icon: FlaskConical,
    overline: 'Pillar IV',
    title: 'Recorded, not recited.',
    body:
      'Vedic Biophysics begins where tradition has named the effect and ends where the sensor confirms it. We carry both readings — the scripture and the spectrometer — and only ship the molecule when both agree.',
  },
];

const VedicBiophysics = ({
  testId      = 'vedic-biophysics',
  overlineHi  = 'The Scientific Pillar · Vedic Biophysics',
  variant     = 'light',           // 'light' | 'dark'
}) => {
  const isDark = variant === 'dark';
  const bgCls  = isDark ? 'bg-[#7C9CB1] text-[#F4F8F9]' : 'bg-[#F4F8F9] text-[#7C9CB1]';
  const overlineCls = isDark ? 'text-[#F4F8F9]/75' : 'text-[#90B0BF]';
  const cardBg = isDark ? 'bg-[#7C9CB1] border-[#F4F8F9]/30' : 'bg-[#F4F8F9] border-[#C1D7E2]';
  const subtleText = isDark ? 'text-[#F4F8F9]/85' : 'text-[#7C9CB1]/85';
  const accentText = isDark ? 'text-[#F4F8F9]/80' : 'text-[#90B0BF]';
  const ruleColor  = isDark ? 'border-[#F4F8F9]/25' : 'border-[#C1D7E2]';

  return (
    <section
      id="vedic-biophysics"
      data-testid={testId}
      className={`px-6 md:px-12 py-24 md:py-32 border-t ${ruleColor} ${bgCls}`}
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16">
          <div className="md:col-span-7">
            <p
              data-testid={`${testId}-overline`}
              className={`font-mono text-[11px] md:text-[13px] tracking-[0.34em] uppercase font-semibold ${overlineCls} mb-6 flex items-center gap-3`}
            >
              <span className={`inline-block w-10 h-[2px] ${isDark ? 'bg-[#F4F8F9]' : 'bg-[#D4AF5A]'} align-middle`} />
              {overlineHi}
            </p>
            <h2 className={`font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] ${isDark ? 'text-[#F4F8F9]' : 'text-[#7C9CB1]'}`}>
              Where scripture
              <br />
              meets the <em className={accentText}>spectrometer</em>.
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-4">
            <p
              data-testid={`${testId}-thesis`}
              className={`font-serif italic text-lg md:text-xl leading-relaxed max-w-md ${subtleText}`}
            >
              Vedic Biophysics is the study of how ancestral botanical
              signatures and circadian light frequencies interact with
              human biological systems.
            </p>
            <p className={`mt-5 font-light leading-relaxed max-w-md ${subtleText}`}>
              It is the scientific pillar of the house. Atreya — our Vedic
              Ritual Optimizer — proposes every regimen from this frame,
              not from cosmetic taste.
            </p>
          </div>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <li
                key={p.id}
                data-testid={`${testId}-pillar-${p.id}`}
                className={`relative border p-7 md:p-8 flex flex-col ${cardBg}`}
              >
                <span
                  aria-hidden
                  className="absolute top-0 left-7 right-7 h-px"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
                  }}
                />
                <p className={`font-mono text-[10px] tracking-[0.3em] uppercase ${overlineCls} mb-5`}>
                  {p.overline}
                </p>
                <Icon size={22} strokeWidth={1.2} className={`${isDark ? 'text-[#F4F8F9]' : 'text-[#7C9CB1]'} mb-5`} />
                <h3 className={`font-serif text-xl md:text-2xl font-light leading-tight ${isDark ? 'text-[#F4F8F9]' : 'text-[#7C9CB1]'}`}>
                  {p.title}
                </h3>
                <p className={`mt-4 text-sm font-light leading-relaxed ${subtleText}`}>
                  {p.body}
                </p>
              </li>
            );
          })}
        </ol>

        <p
          data-testid={`${testId}-signature`}
          className={`mt-14 font-mono text-[10px] tracking-[0.3em] uppercase ${overlineCls} text-center`}
        >
          The Vedas named the effect · the spectrometer confirms it · Atreya prescribes the hour
        </p>
      </div>
    </section>
  );
};

export default VedicBiophysics;
